import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Zap, Plus, Pencil, Trash2 } from 'lucide-react'
import { getDrinkers, createDrinker, updateDrinker, deleteDrinker } from '../api/brainer'
import { SearchInput } from '../components/SearchInput'
import { Modal } from '../components/Modal'
import { EmptyState } from '../components/EmptyState'
import { ConfirmDialog } from '../components/ConfirmDialog'
import { useDateFmt } from '../hooks/useDateFmt'
import { Field } from '../components/Field'
import { Button } from '../components/ui/button'
import type { BDrinker } from '../types'

export function BrainerDrinkers() {
  const fmt = useDateFmt()
  const qc = useQueryClient()
  const { data: drinkers = [], isLoading } = useQuery({
    queryKey: ['b-drinkers'],
    queryFn: () => getDrinkers().then(r => r.data),
  })

  const [showCreate, setShowCreate] = useState(false)
  const [editTarget, setEditTarget] = useState<BDrinker | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<BDrinker | null>(null)

  const [form, setForm] = useState({ title: '', script: '', resultKey: '' })

  const openCreate = () => { setForm({ title: '', script: '', resultKey: '' }); setShowCreate(true) }
  const openEdit = (d: BDrinker) => { setForm({ title: d.title, script: d.script, resultKey: d.resultKey }); setEditTarget(d) }

  const create = useMutation({
    mutationFn: () => createDrinker(form),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['b-drinkers'] }); setShowCreate(false) },
  })

  const save = useMutation({
    mutationFn: () => updateDrinker(editTarget!.id, form),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['b-drinkers'] }); setEditTarget(null) },
  })

  const del = useMutation({
    mutationFn: (id: string) => deleteDrinker(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['b-drinkers'] }); setDeleteTarget(null) },
  })

  const formValid = form.title.trim()

  const [search, setSearch] = useState('')
  const filtered = (drinkers as BDrinker[]).filter(d =>
    [d.title, d.resultKey, d.script].some(v => v.toLowerCase().includes(search.toLowerCase()))
  )

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-ink">Drinkers</h1>
          <p className="text-sm text-ink-dim mt-0.5">Reusable JS scripts — assign them to drinker steps</p>
        </div>
        <div className="flex items-center gap-2">
          <SearchInput value={search} onChange={setSearch} placeholder="Search drinkers" />
          <Button icon={Plus} onClick={openCreate}>New drinker</Button>
        </div>
      </div>

      {isLoading ? (
        <div className="p-8 text-center text-sm text-ink-dim">Loading</div>
      ) : drinkers.length === 0 ? (
        <EmptyState icon={Zap} title="No drinkers" description="Create reusable JS scripts that transform journey payload data" />
      ) : filtered.length === 0 ? (
        <EmptyState icon={Zap} title="No matching drinkers" description="Try a different search" />
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th>Title</th>
              <th>Result key</th>
              <th>Script preview</th>
              <th>Updated</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {filtered.map(d => (
              <tr key={d.id}>
                <td className="font-medium text-ink">{d.title}</td>
                <td>
                  <span className="font-mono text-xs bg-amber-50 text-amber-700 px-1.5 py-0.5 rounded">{d.resultKey}</span>
                </td>
                <td className="text-ink-dim font-mono text-xs max-w-xs truncate">
                  {d.script.split('\n')[0].slice(0, 60) || '—'}
                </td>
                <td className="text-ink-dim">{fmt(d.updatedAt)}</td>
                <td>
                  <div className="flex items-center gap-3 justify-end">
                    <button onClick={() => openEdit(d)} className="text-ink-dim hover:text-ink transition-colors"><Pencil size={13} /></button>
                    <button onClick={() => setDeleteTarget(d)} className="text-ink-dim hover:text-red-500 transition-colors"><Trash2 size={13} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {(showCreate || editTarget) && (
        <Modal
          title={editTarget ? `Edit — ${editTarget.title}` : 'New drinker'}
          onClose={() => { setShowCreate(false); setEditTarget(null) }}
          size="lg"
        >
          <DrinkerForm form={form} onChange={setForm} />
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="secondary" onClick={() => { setShowCreate(false); setEditTarget(null) }}>Cancel</Button>
            <Button
              onClick={() => (editTarget ? save : create).mutate()}
              disabled={!formValid || create.isPending || save.isPending}
              loading={create.isPending || save.isPending}
            >
              {editTarget ? 'Save' : 'Create'}
            </Button>
          </div>
        </Modal>
      )}

      {deleteTarget && (
        <ConfirmDialog
          title="Delete drinker"
          message={`Delete drinker "${deleteTarget.title}"? Steps using it will lose their drinker assignment.`}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={() => del.mutate(deleteTarget.id)}
          loading={del.isPending}
        />
      )}
    </div>
  )
}

function DrinkerForm({
  form,
  onChange,
}: {
  form: { title: string; script: string; resultKey: string }
  onChange: (f: { title: string; script: string; resultKey: string }) => void
}) {
  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    onChange({ ...form, [k]: e.target.value })

  return (
    <div className="space-y-4">
      <Field label="Title *">
        <input value={form.title} onChange={set('title')} placeholder="e.g. Build full name" className="field" />
      </Field>
      <Field label="Script">
        <div className="text-xs text-ink-dim mb-2 space-y-0.5">
          <p>Async JS. Use <code className="bg-paper-sunken px-1 rounded">await</code> freely. Journey payload keys are in scope.</p>
          <p className="font-mono text-theme-xs leading-5 bg-paper-sunken border border-paper-border rounded p-2 mt-1 whitespace-pre">
{`ctx.findCup(title)                             → { id, title, traits }
ctx.findCoffees(cupTitle)                      → [{ id, beans }]
ctx.findCoffee(cupTitle, { TraitName: value }) → { id, beans } | null
ctx.getCoffee(id)                              → { id, beans } | null
ctx.createCoffee(cupTitle, { TraitName: val }) → { id }
ctx.updateBean(coffeeId, traitTitle, value)
ctx.deleteBean(coffeeId, traitTitle)
ctx.deleteCoffee(id)
ctx.getParent(coffeeId, parentCupTitle)        → { id, beans } | null
ctx.getChildren(coffeeId, childCupTitle)       → [{ id, beans }]
ctx.addFlavor(parentId, childId, label)
ctx.deleteFlavor(parentId, childId, label)
ctx.log(message)                               → captured in step logs`}
          </p>
        </div>
        <textarea
          value={form.script}
          onChange={set('script')}
          rows={12}
          placeholder={`// Example: upsert a customer and tag them as returning
const existing = await ctx.findCoffee('Customers', { Email: payload.email });
if (existing) {
  await ctx.updateBean(existing.id, 'Status', 'returning');
  return { customerId: existing.id };
}
const c = await ctx.createCoffee('Customers', { Email: payload.email, Status: 'new' });
return { customerId: c.id };`}
          className="field font-mono resize-none"
        />
      </Field>
      <Field label="Result key">
        <input value={form.resultKey} onChange={set('resultKey')} placeholder="e.g. customerId (optional)" className="field" />
        <p className="text-xs text-ink-dim mt-1">The return value of the script is stored in the journey payload under this key. Leave empty for side-effect-only scripts.</p>
      </Field>
    </div>
  )
}
